import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { eq } from "@-/com.npmjs.drizzle-orm";
import { ensureUserSlideRow } from "@-/dev.improve.haniverse.lib.slide.row";
import { useCallback } from "react";

/**
 * Returns a callback that toggles whether an authored slide is favorited in the
 * `user_slide` table (keyed by its catalogue UUID `key`), so the user can come back
 * to it later from the Favorite Slides settings page.
 *
 * A slide's `user_slide` row holds both `completedAt` and `favoritedAt`
 * independently, so this only ever touches `favoritedAt` — favoriting an
 * un-completed slide must NOT mark it complete, and un-favoriting must leave a
 * completed slide completed.
 *
 * `actorDb.query` filters soft-deleted rows, so `existing` is always the live row
 * (or `undefined`). Toggling therefore reduces to three cases: no live row →
 * create a blank one (`ensureUserSlideRow`) and favorite it; live + not favorited → set `favoritedAt`; live +
 * favorited → clear `favoritedAt`, and soft-delete the row when it carries no
 * `completedAt` either, so an un-favorited never-completed slide doesn't linger as
 * an empty row (matching the tombstone pattern in `useResetOnboarding`).
 *
 * Idempotent and self-toggling: it reads the current row and flips state, so the
 * caller doesn't have to know whether the slide is currently favorited. Wrapping
 * the async work in `useMutation` keeps lifecycle / error handling uniform with the
 * rest of the codebase; the returned callback resolves a Promise so callers can
 * `await` it.
 */
export function useToggleSlideFavorite() {
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);

  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({ key }: { key: string }): Promise<void> => {
      const nowIso = new Date().toISOString();

      await actorDb(import.meta.location).transaction(async (tx) => {
        const existing = await tx.query.userSlide.findFirst({ where: { key } });

        if (existing?.favoritedAt != null) {
          // Un-favorite: clear `favoritedAt`. If the row carries no `completedAt`
          // either, soft-delete it so it doesn't linger as an empty row.
          await tx
            .update(actorSchema.userSlide)
            .set(
              existing.completedAt === null
                ? { deletedAt: nowIso, favoritedAt: null }
                : { favoritedAt: null },
            )
            .where(eq(actorSchema.userSlide.id, existing.id));
          return;
        }

        // Favorite the slide's row, creating it blank first when there is none.
        const id = await ensureUserSlideRow(tx, key, nowIso);
        await tx
          .update(actorSchema.userSlide)
          .set({ favoritedAt: nowIso })
          .where(eq(actorSchema.userSlide.id, id));
      });
    },
    mutationKey: ["toggleSlideFavorite"],
  });

  const mutateAsync = toggleFavoriteMutation.mutateAsync;
  return useCallback((key: string) => mutateAsync({ key }), [mutateAsync]);
}
